import React, { useState, useEffect, useCallback } from 'react';
import { 
  getMailchimpIntegrationStatus, 
  initiateMailchimpOAuth, 
  disconnectMailchimp,
  getMailchimpLists,
  syncMailchimpLists,
  IntegrationStatus 
} from '../../api/integrations';
import { toast } from 'sonner';
import LoadingComponent from '../LoadingComponent';
import moment from 'moment';

interface MailchimpIntegrationProps {
  communityId: string;
  userId: string;
  onClose?: () => void;
}

interface MailchimpList {
  id: string;
  name: string;
  memberCount: number;
}

const MailchimpIntegration: React.FC<MailchimpIntegrationProps> = ({ 
  communityId, 
  userId, 
  onClose 
}) => {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [lists, setLists] = useState<MailchimpList[]>([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const integrationStatus = await getMailchimpIntegrationStatus(communityId, userId);
      setStatus(integrationStatus);
    } catch (error) {
      console.error('Error loading Mailchimp status:', error);
      toast.error('Failed to load Mailchimp integration status');
    } finally {
      setIsLoading(false);
    }
  }, [communityId, userId]);

  const loadLists = useCallback(async () => {
    try {
      setIsLoadingLists(true);
      const listsData = await getMailchimpLists(communityId, userId);
      setLists(listsData.lists || []);
    } catch (error) {
      console.error('Error loading Mailchimp lists:', error);
      toast.error('Failed to load Mailchimp lists');
    } finally {
      setIsLoadingLists(false);
    }
  }, [communityId, userId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.connected) {
      loadLists();
    }
  }, [status?.connected, loadLists]);

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      const response = await initiateMailchimpOAuth(communityId, userId);
      if (response.authUrl) {
        // Open OAuth flow in new window
        const popup = window.open(
          response.authUrl,
          'Mailchimp OAuth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        // Listen for OAuth completion
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            setIsConnecting(false);
            loadStatus(); // Refresh status
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error connecting Mailchimp:', error);
      toast.error('Failed to initiate Mailchimp connection');
      setIsConnecting(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      await syncMailchimpLists(communityId, userId);
      toast.success('Mailchimp lists synced successfully');
      await loadLists();
    } catch (error) {
      console.error('Error syncing Mailchimp lists:', error);
      toast.error('Failed to sync Mailchimp lists');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Mailchimp?')) {
      return;
    }

    try {
      await disconnectMailchimp(communityId, userId);
      toast.success('Mailchimp disconnected successfully');
      await loadStatus();
      setLists([]);
    } catch (error) {
      console.error('Error disconnecting Mailchimp:', error);
      toast.error('Failed to disconnect Mailchimp');
    }
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <div className="w-full p-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Mailchimp Integration</h2>
          {status?.connected && (
            <div className="flex gap-2">
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="px-4 py-2 text-sm font-medium text-primary-200 hover:text-primary-300 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50"
              >
                {isSyncing ? 'Syncing...' : 'Sync Lists'}
              </button>
              <button
                onClick={handleDisconnect}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50"
              >
                Disconnect
              </button>
            </div>
          )}
        </div>

        {status?.connected ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-green-800 font-medium">Connected</span>
              </div>
            </div>

            {status.accountName && (
              <div className="text-sm text-gray-600">
                <p><strong>Account:</strong> {status.accountName}</p>
                {status.lastSyncedAt && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Last Synced</p>
                      <p className="text-sm text-gray-700 font-semibold">{moment(status.lastSyncedAt).fromNow()}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{moment(status.lastSyncedAt).format('MMM D, YYYY [at] h:mm A')}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Email Lists */}
            {isLoadingLists ? (
              <LoadingComponent />
            ) : (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Email Lists</h3>
                {lists.length > 0 ? (
                  <ul className="space-y-2 max-h-64 overflow-y-auto">
                    {lists.map((list) => (
                      <li key={list.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div>
                          <span className="text-sm font-medium text-gray-900">{list.name}</span>
                          <p className="text-xs text-gray-500">{list.memberCount} members</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-center py-4">No email lists found</p>
                )}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Available Features</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Sync email lists and campaigns</li>
                <li>• View and manage subscriber lists</li>
                <li>• Access campaign analytics</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Connect your Mailchimp account to sync email lists and campaigns directly from your dashboard.
            </p>
            <button
              onClick={handleConnect}
              disabled={isConnecting}
              className="px-6 py-3 bg-primary-200 hover:bg-primary-300 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? 'Connecting...' : 'Connect Mailchimp'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MailchimpIntegration;

